import {
  DecreeAnalyzed,
  DecreeExtended,
  DecreeFilter,
  DecreeStatus,
  arrDecreeAnalysedValues,
  arrDecreeCategoryValues,
  arrDecreeExtendedValues,
  arrDecreeStatusValues,
} from "./decree.const";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDownIcon } from "components/icons";
import { DecreeCategory } from "@/types/gql/graphql";
import { Input } from "@/components/ui/input";
import { SearchIcon } from "lucide-react";
import { useDebouncedCallback } from "hooks/use-debounce-callback";
import useToggle from "@/hooks/use-toggle";

export type FilterDecreeProps = {
  filter: DecreeFilter;
  onChange: (input: DecreeFilter) => void;
  handleCreateDecree: () => void;
};

export const FilterDecree = ({ onChange, filter, handleCreateDecree }: FilterDecreeProps) => {
  const [open, setOpen] = useToggle();

  const changeFilter = (
    val: DecreeStatus | DecreeAnalyzed | DecreeCategory | DecreeExtended,
    type: keyof Omit<DecreeFilter, "searchString">,
  ) => {
    const statusExists = filter[type].findIndex((item) => item === val);
    const newStatus = [...filter[type]];
    if (statusExists > -1) {
      newStatus.splice(statusExists, 1);
      onChange({ ...filter, [type]: newStatus });
    } else {
      onChange({ ...filter, [type]: [...newStatus, val] });
    }
  };

  const handleSearch = useDebouncedCallback((value: string) => {
    onChange({ ...filter, searchString: value });
  });

  const handleSearchChange = (value: string) => {
    handleSearch(value);
  };

  return (
    <div className="flex gap-4">
      <div className="relative">
        <SearchIcon className="size-4 absolute top-2.5 left-2" />
        <Input
          onChange={(event) => handleSearchChange(event.target.value)}
          placeholder="Recherche de décrets"
          className="w-50 pl-8"
        />
      </div>
      <div className="flex">
        <Popover>
          <PopoverTrigger asChild>
            <Button onClick={setOpen} className="bg-none" variant="outline">
              <span className="text-medium font-medium text-primary">Filter decree</span>
              <ChevronDownIcon
                className={`${open && "rotate-180 transform"}
                  ml-2 transition duration-150 ease-in-out`}
                aria-hidden="true"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[500px]">
            <div className="flex justify-between">
              <div className="gap-2 flex flex-col">
                {arrDecreeCategoryValues.map((item, itemIdx) => (
                  <div className="flex items-center space-x-2" key={`multi-select-${item}-${itemIdx}`}>
                    <Checkbox
                      checked={filter.category.includes(item.value as DecreeCategory)}
                      onClick={() => changeFilter(item.value as DecreeCategory, "category")}
                      value={item.value}
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {item.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="gap-2 flex flex-col">
                {arrDecreeStatusValues.map((item, itemIdx) => (
                  <div className="flex items-center space-x-2" key={`multi-select-${item}-${itemIdx}`}>
                    <Checkbox
                      checked={filter.status.includes(item)}
                      onClick={() => changeFilter(item, "status")}
                      value={item}
                    />
                    <label
                      htmlFor="terms"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {item}
                    </label>
                  </div>
                ))}
              </div>
              <div className="gap-2 flex flex-col">
                {arrDecreeAnalysedValues.map((item, itemIdx) => (
                  <div className="flex items-center space-x-2" key={`multi-select-${item}-${itemIdx}`}>
                    <Checkbox
                      checked={filter.analyzed.includes(item)}
                      onClick={() => changeFilter(item, "analyzed")}
                      value={item}
                      id={item}
                    />
                    <label
                      htmlFor={item}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {item}
                    </label>
                  </div>
                ))}
              </div>
              <div className="gap-2 flex flex-col">
                {arrDecreeExtendedValues.map((item, itemIdx) => (
                  <div className="flex items-center space-x-2" key={`multi-select-${item}-${itemIdx}`}>
                    <Checkbox
                      checked={filter.extended.includes(item)}
                      onClick={() => changeFilter(item, "extended")}
                      value={item}
                      id={item}
                    />
                    <label
                      htmlFor={item}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {item}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Button onClick={handleCreateDecree}>Create</Button>
    </div>
  );
};
